import { AlertCircle, Check, Info } from 'lucide-react'
import { useState } from 'react'

/* The release builder, recreated from the running studio.
 *
 * This is the surface a prospect's artists touch every week, so it is the
 * surface the platform gets judged on. An earlier version of this file
 * invented a two-panel form-and-findings layout that looked nothing like the
 * product and was considerably worse than it — the wrong way round for a
 * graphic whose whole job is to make someone want the thing.
 *
 * What the real builder actually is, top to bottom:
 *   - a breadcrumb bar with the release status badge pinned right
 *   - a release header card: artwork, title, artist, a release-type chip, and
 *     a stat row (tracks / runtime / release date / stores)
 *   - three tabs — Release, Tracks (carrying a count), Distribution
 *   - a form panel of titled SECTIONS, each with an inline hint, fields laid
 *     out two-up, and required fields marked with a small brand-coloured dot
 *     rather than an asterisk
 *   - an action bar: Save / Discard / Validate on the left, the primary action
 *     on the right, and one line explaining that Submit only opens once
 *     Validate reports no blocking problems
 *
 * That last line is the product's actual pitch and it is written on the real
 * screen, so it is written here.
 *
 * Measured values: shell #050506, panels #0a0a0a, border white@8%, radius 8px,
 * display face Space Grotesk. The required-field dot and the primary action
 * carry the tenant's colour; everything else is neutral, which is what the
 * product does.
 *
 * The findings panel only appears after Validate runs, but it is shown here
 * because it is the state a reader learns the most from — and clicking a
 * finding jumps to the field that raised it, which is what it does in the
 * product. A mock whose interactions differ from the real ones teaches the
 * wrong thing.
 *
 * THE EXAMPLES ARE DETERMINISTIC RULES, and that boundary matters. This panel
 * is the shipped pre-submit validation: arithmetic and format checks a machine
 * settles on its own, like composer shares that must total 100%. It is NOT the
 * agentic QC that reads a release, spots an ambiguity and asks the uploader
 * about it — that engine is in build and lives on its own page, correctly
 * badged. An example needing a judgement call would read as that second thing
 * being available today.
 *
 * If studio/src/builder changes shape, change this with it.
 */

type Tab = 'release' | 'tracks' | 'distribution'

const ACCENT = '#7c3aed'
const PANEL = '#0a0a0a'
const BORDER = 'rgba(255,255,255,0.08)'

const TABS: { key: Tab; label: string; count?: number }[] = [
  { key: 'release', label: 'Release' },
  { key: 'tracks', label: 'Tracks', count: 9 },
  { key: 'distribution', label: 'Distribution' },
]

interface Finding {
  field: string
  message: string
  severity: 'blocking' | 'advisory'
  tab: Tab
}

const FINDINGS: Finding[] = [
  {
    field: 'composers[1].share',
    message: 'Composer shares total 90%. They must total exactly 100% before submission.',
    severity: 'blocking',
    tab: 'tracks',
  },
  {
    field: 'tracks[2].isrc',
    message: 'Track 3 has no ISRC. One will be assigned on submit unless you supply your own.',
    severity: 'advisory',
    tab: 'tracks',
  },
  {
    field: 'sale_date',
    message: 'Release date is 6 days out. Editorial pitching closes at 7 days.',
    severity: 'advisory',
    tab: 'distribution',
  },
]

// Counted once at module load. As a render-body filter this re-scanned a
// module-level constant on every render for a number that cannot change.
const BLOCKING_COUNT = FINDINGS.filter((f) => f.severity === 'blocking').length

interface Field {
  label: string
  value: string
  required?: boolean
  optional?: boolean
  /** Ties the field to the finding that highlights it. */
  field?: string
  /** Renders full width rather than in the two-up grid. */
  wide?: boolean
}

const SECTIONS: Record<Tab, { title: string; hint: string; fields: Field[] }[]> = {
  release: [
    {
      title: 'Title',
      hint: 'Release type is assigned automatically — see the header above.',
      fields: [
        { label: 'Release title', value: 'Slow Monsoon (feat. Osei)', required: true },
        { label: 'Version', value: 'e.g. Deluxe, Radio Edit', optional: true },
        { label: 'Metadata language', value: 'English', required: true, wide: true },
      ],
    },
    {
      title: 'Artists',
      hint: 'One shared catalog feeds every role.',
      fields: [
        { label: 'Main primary artist', value: 'Ana Reyes', required: true },
        { label: 'Featured artists', value: 'Osei — featured' },
      ],
    },
  ],
  tracks: [
    {
      title: 'Tracks',
      hint: 'Audio is read at the header level the moment it arrives.',
      fields: [
        { label: '1 · Slow Monsoon', value: 'WAV · 44.1 kHz / 24-bit · 3:48' },
        { label: '2 · Paper Radio', value: 'WAV · 44.1 kHz / 24-bit · 4:12' },
        { label: '3 · Held Note', value: 'ISRC not set', field: 'tracks[2].isrc' },
        { label: 'Composers', value: '2 writers · shares total 90%', field: 'composers[1].share', wide: true },
      ],
    },
  ],
  distribution: [
    {
      title: 'Release and territories',
      hint: 'Stores are the ones enabled for this account.',
      fields: [
        { label: 'Release date', value: '14 March 2026', required: true, field: 'sale_date' },
        { label: 'Territories', value: 'Worldwide', required: true },
        { label: 'Stores', value: '38 of 41 enabled', wide: true },
      ],
    },
  ],
}

const TONE = {
  blocking: {
    icon: AlertCircle,
    chip: 'text-rose-300',
    ring: 'border-rose-400/40 bg-rose-400/[0.07]',
    label: 'Blocking',
  },
  advisory: {
    icon: Info,
    chip: 'text-amber-300',
    ring: 'border-amber-400/40 bg-amber-400/[0.07]',
    label: 'Advisory',
  },
} as const

export default function ReleaseBuilderMockup() {
  const [tab, setTab] = useState<Tab>('release')
  const [focused, setFocused] = useState<string | null>(null)

  return (
    <div className="rounded-[1.75rem] border border-white/[0.08] bg-gradient-to-b from-white/[0.05] to-white/[0.02] p-5 sm:p-8">
      <div className="mb-6 text-center">
        <p className="font-mono text-[12px] uppercase tracking-[0.16em] text-[#ff8c4d]">
          The release builder
        </p>
        <h3
          className="mx-auto mt-2 max-w-xl text-lg font-semibold leading-snug text-white sm:text-xl"
          style={{ fontFamily: 'var(--wavbee-font-display)' }}
        >
          Problems surface here, not in an email three days later.
        </h3>
      </div>

      <div
        className="overflow-hidden rounded-xl border shadow-[0_24px_60px_rgba(0,0,0,0.45)]"
        style={{ background: '#050506', borderColor: 'rgba(255,255,255,0.1)' }}
      >
        <div className="flex flex-col gap-2.5 p-3">
          {/* Breadcrumb bar, status badge pinned right. */}
          <div
            className="flex items-center justify-between gap-3 rounded-lg border px-4 py-2.5"
            style={{ background: PANEL, borderColor: BORDER }}
          >
            <p className="truncate text-[12px] text-white/45">
              Releases <span className="text-white/25">/</span>{' '}
              <span className="text-white/80">Slow Monsoon</span>
            </p>
            <span
              className="shrink-0 rounded-md border px-2 py-0.5 text-[11px] font-medium text-white/70"
              style={{ borderColor: 'rgba(255,255,255,0.16)' }}
            >
              Draft
            </span>
          </div>

          {/* Release header: artwork, title, chip, stat row. */}
          <div
            className="flex gap-4 rounded-lg border p-4"
            style={{ background: PANEL, borderColor: BORDER }}
          >
            <div
              className="hidden h-[88px] w-[88px] shrink-0 rounded-md sm:block"
              style={{ background: `linear-gradient(150deg, ${ACCENT}cc, ${ACCENT}33 60%, #14141a)` }}
              aria-hidden="true"
            />
            <div className="min-w-0 flex-1">
              <p
                className="truncate text-lg font-semibold text-[#f5f5fa]"
                style={{ fontFamily: 'var(--wavbee-font-display)' }}
              >
                Slow Monsoon
              </p>
              <p className="mt-1 flex flex-wrap items-center gap-2 text-[12px] text-white/55">
                Ana Reyes
                <span
                  className="rounded border px-1.5 py-0.5 text-[10px] text-white/50"
                  style={{ borderColor: BORDER }}
                >
                  EP · auto
                </span>
              </p>
              <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
                {[
                  { k: 'Tracks', v: '9' },
                  { k: 'Runtime', v: '32:14' },
                  { k: 'Release date', v: '2026-03-14' },
                  { k: 'Stores', v: '38' },
                ].map((stat) => (
                  <div key={stat.k}>
                    <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-white/35">
                      {stat.k}
                    </p>
                    <p className="mt-0.5 text-[13px] font-semibold tabular-nums text-white/90">
                      {stat.v}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Tabs. */}
          <div
            className="flex w-fit gap-1 rounded-lg border p-1"
            style={{ background: PANEL, borderColor: BORDER }}
            role="group"
            aria-label="Builder sections"
          >
            {TABS.map((entry) => {
              const current = tab === entry.key
              return (
                <button
                  key={entry.key}
                  type="button"
                  aria-pressed={current}
                  onClick={() => setTab(entry.key)}
                  className="flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[12px] font-medium transition-colors"
                  style={
                    current
                      ? { background: 'rgba(255,255,255,0.075)', color: '#fff' }
                      : { color: 'rgba(255,255,255,0.45)' }
                  }
                >
                  {entry.label}
                  {entry.count !== undefined && (
                    <span className="rounded bg-white/[0.08] px-1 text-[10px] tabular-nums text-white/55">
                      {entry.count}
                    </span>
                  )}
                </button>
              )
            })}
          </div>

          <div className="grid gap-2.5 lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)]">
            {/* Form panel. */}
            <div
              className="flex flex-col gap-5 rounded-lg border p-4"
              style={{ background: PANEL, borderColor: BORDER }}
            >
              {SECTIONS[tab].map((section) => (
                <div key={section.title}>
                  <p className="text-[13px] font-semibold text-white">
                    {section.title}
                    <span className="ml-2 text-[11px] font-normal text-white/40">
                      {section.hint}
                    </span>
                  </p>
                  <div className="mt-3 grid gap-2.5 sm:grid-cols-2">
                    {section.fields.map((field) => {
                      const flagged = field.field && focused === field.field
                      return (
                        <div key={field.label} className={field.wide ? 'sm:col-span-2' : undefined}>
                          <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-white/40">
                            {field.label}
                            {field.required && (
                              <span
                                className="h-1.5 w-1.5 rounded-full"
                                style={{ background: ACCENT }}
                                aria-label="required"
                              />
                            )}
                            {field.optional && (
                              <span className="normal-case text-white/25">optional</span>
                            )}
                          </p>
                          <div
                            className="mt-1 truncate rounded-md border px-2.5 py-2 text-[12px] transition-all"
                            style={
                              flagged
                                ? {
                                    borderColor: 'rgba(244,63,94,0.55)',
                                    background: 'rgba(244,63,94,0.07)',
                                    color: '#fff',
                                  }
                                : {
                                    borderColor: BORDER,
                                    background: 'rgba(255,255,255,0.02)',
                                    color: 'rgba(255,255,255,0.75)',
                                  }
                            }
                          >
                            {field.value}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              ))}
            </div>

            {/* Validation panel. */}
            <div className="rounded-lg border p-4" style={{ background: PANEL, borderColor: BORDER }}>
              <div className="flex items-center justify-between gap-2">
                <p className="text-[13px] font-semibold text-white">Validation</p>
                <span className="text-[10px] text-white/35">
                  {FINDINGS.length} findings
                </span>
              </div>
              <p className="mt-1 text-[11px] leading-relaxed text-white/40">
                Click one to jump to the field that raised it.
              </p>

              <div className="mt-3 flex flex-col gap-2">
                {FINDINGS.map((finding) => {
                  const tone = TONE[finding.severity]
                  const Icon = tone.icon
                  return (
                    <button
                      key={finding.field}
                      type="button"
                      onClick={() => {
                        setTab(finding.tab)
                        setFocused(finding.field)
                      }}
                      className={`w-full rounded-md border p-2.5 text-left transition-all hover:brightness-125 ${tone.ring}`}
                    >
                      <span className="flex items-center gap-1.5">
                        <Icon className={`h-3 w-3 shrink-0 ${tone.chip}`} aria-hidden="true" />
                        <span
                          className={`text-[10px] uppercase tracking-[0.12em] ${tone.chip}`}
                        >
                          {tone.label}
                        </span>
                        <span className="ml-auto truncate text-[10px] text-white/25">
                          {finding.field}
                        </span>
                      </span>
                      <span className="mt-1.5 block text-[11px] leading-relaxed text-white/60">
                        {finding.message}
                      </span>
                    </button>
                  )
                })}
              </div>
            </div>
          </div>

          {/* Action bar. */}
          <div
            className="flex flex-wrap items-center justify-between gap-3 rounded-lg border px-4 py-3"
            style={{ background: PANEL, borderColor: BORDER }}
          >
            <div className="flex items-center gap-1.5">
              {['Save', 'Discard', 'Validate'].map((label) => (
                <span
                  key={label}
                  className="rounded-md border px-2.5 py-1.5 text-[11px] font-medium text-white/70"
                  style={{ borderColor: BORDER }}
                >
                  {label}
                </span>
              ))}
            </div>
            <div className="flex items-center gap-3">
              <p className="hidden max-w-[16rem] text-right text-[10px] leading-relaxed text-white/35 lg:block">
                Submit opens once Validate reports no blocking problems.
              </p>
              <span
                className="flex items-center gap-1.5 rounded-md border border-white/15 px-3 py-1.5 text-[12px] font-semibold text-white/40"
                style={{ background: 'rgba(255,255,255,0.04)' }}
              >
                <Check className="h-3 w-3" aria-hidden="true" />
                Submit release
              </span>
            </div>
          </div>
        </div>
      </div>

      <p className="mt-5 text-center text-[0.75rem] leading-relaxed text-white/45">
        Recreated from the running builder — three tabs, sections with inline hints, required fields
        marked with a dot, and two finding severities. Submit stays locked while{' '}
        {BLOCKING_COUNT === 1 ? 'the one blocking finding stands' : `${BLOCKING_COUNT} blocking findings stand`};
        advisories never block.
      </p>
    </div>
  )
}
